// actions.ts
import { useUserService } from "~/composables/service/useUserService";
import { useUtilsStore } from "~/store/utils/utilsStore";

// store 인스턴스별 in-flight fetch 를 공유한다. 모듈 스코프 단일 변수는 SSR 에서
// 요청 간 store 가 결합되고(다른 요청의 store 를 갱신하지 못함) 여러 Pinia 인스턴스/HMR
// 에서도 어긋나므로, store 를 키로 하는 WeakMap 으로 격리한다.
const inflightFetches = new WeakMap<object, Promise<void>>();

export const actions = {
    updateBlockedDomains(emailList: string[]) {
        const store = useUtilsStore();

        const normalized = Array.from(
            new Set(
                (emailList ?? [])
                    .map((d) => String(d).trim().toLowerCase())
                    .filter(Boolean)
            )
        );

        store.blockedEmailDomains = normalized;
        store.blockedEmailDomainsLoaded = true;
        store.blockedEmailDomainsError = null;
    },

    async fetchBlockedEmailDomains(force = false) {
        const store = useUtilsStore();

        // 이미 진행 중인 요청이 있으면 그 promise 를 공유한다. 이렇게 해야
        // `await fetchBlockedEmailDomains()` 하는 소비처가 실제 요청 완료까지
        // 기다린 뒤 isBlockedEmailDomain() 을 읽을 수 있다(빈 리스트 오판 방지).
        // force 도 새 요청을 중복 실행하지 않고 진행 중 요청을 공유한다.
        const existing = inflightFetches.get(store);
        if (existing) return existing;

        // 성공적으로 로드된 뒤에는(빈 목록 포함) 재요청하지 않는다. length 대신
        // loaded 를 기준으로 해야 서버가 빈 목록을 반환한 경우 매번 재fetch 하지 않는다.
        if (!force && store.blockedEmailDomainsLoaded) return;

        const request = (async () => {
            store.blockedEmailDomainsLoading = true;
            store.blockedEmailDomainsError = null;

            try {
                const userService = useUserService();
                const list = await userService.getBlockedEmailDomains();
                store.updateBlockedDomains(list);
            } catch (e: any) {
                store.blockedEmailDomainsError =
                    e?.message ?? "failed_to_fetch_blocked_email_domains";
                store.blockedEmailDomainsLoaded = false;
            } finally {
                store.blockedEmailDomainsLoading = false;
                if (inflightFetches.get(store) === request) {
                    inflightFetches.delete(store);
                }
            }
        })();

        inflightFetches.set(store, request);
        return request;
    },

    isBlockedEmailDomain(email: string | null | undefined, forceLoad = false): boolean {
        const store = useUtilsStore();

        const needsFetch =
            !store.blockedEmailDomainsLoaded &&
            !store.blockedEmailDomainsLoading &&
            (forceLoad || !store.blockedEmailDomainsError);

        if (needsFetch) {
            store.fetchBlockedEmailDomains();
            return false;
        }

        if (!email) return false;

        const trimmed = email.trim().toLowerCase();
        const at = trimmed.lastIndexOf("@");
        if (at < 0) return false;

        const domain = trimmed.slice(at + 1);
        if (!domain) return false;

        return store.blockedEmailDomains.includes(domain);
    },
};
